/**
 * Video Editor — Phase B3 module root.
 *
 * Surface 1 (the project list) plus the workspace router that drives surfaces
 * 2–7 (ProjectWorkspace). Wired to the real /api/video-editor backend.
 *
 * The spec's 6-state status model is mapped onto the backend's real 8-state
 * lifecycle; the AI co-pilot (spec §3c) is deferred to its own design pass.
 */
import React, { useState, useEffect, useCallback } from 'react';
import type { User, VideoProject, VideoProjectStatus } from './types';
import { useVideoEditorApi } from './videoEditorApi';
import ProjectWorkspace from './ProjectWorkspace';
import './VideoEditor.css';

// ── Status mapping: real backend lifecycle → spec badge variants ──────
type BadgeVariant = 'drafting' | 'analyzing' | 'ready' | 'rendering' | 'done' | 'failed';

const STATUS_META: Record<VideoProjectStatus, { variant: BadgeVariant; label: string; action: string; icon: string; working: boolean }> = {
  'created':      { variant: 'drafting',  label: 'Draft',      action: 'Continue setup', icon: '✎',  working: false },
  'raw-imported': { variant: 'drafting',  label: 'Footage in', action: 'Start analysis', icon: '🎞', working: false },
  'analysing':    { variant: 'analyzing', label: 'Analyzing',  action: 'Watch progress', icon: '⚙',  working: true  },
  'planning':     { variant: 'analyzing', label: 'Planning',   action: 'Pick a cut',     icon: '✨', working: true  },
  'rendering':    { variant: 'rendering', label: 'Rendering',  action: 'Watch progress', icon: '⚙',  working: true  },
  'in-review':    { variant: 'ready',     label: 'In review',  action: 'Open',           icon: '▶',  working: false },
  'stored':       { variant: 'done',      label: 'Stored',     action: 'Open',           icon: '▶',  working: false },
  'closed':       { variant: 'done',      label: 'Closed',     action: 'Open',           icon: '📁', working: false },
};

interface FilterDef { key: string; label: string; statuses: VideoProjectStatus[] | null; }
const FILTERS: FilterDef[] = [
  { key: 'all',       label: 'All',       statuses: null },
  { key: 'draft',     label: 'Draft',     statuses: ['created', 'raw-imported'] },
  { key: 'analyzing', label: 'Analyzing', statuses: ['analysing', 'planning'] },
  { key: 'rendering', label: 'Rendering', statuses: ['rendering'] },
  { key: 'review',    label: 'In review', statuses: ['in-review'] },
  { key: 'done',      label: 'Done',      statuses: ['stored', 'closed'] },
];

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

const StatusBadge: React.FC<{ status: VideoProjectStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <span className={`status-badge status-badge--${meta.variant}`}>
      <span className="status-badge__dot" /> {meta.label}
    </span>
  );
};

const ProjectCard: React.FC<{ project: VideoProject; onOpen: (id: string) => void }> = ({ project, onOpen }) => {
  const meta = STATUS_META[project.status];
  const brand = project.brand;
  return (
    <button className="s1-card" onClick={() => onOpen(project._id)}>
      <div className={`s1-card__thumb${brand ? ` s1-card__thumb--${brand}` : ''}${meta.working ? ' s1-card__thumb--working' : ''}`}>
        <span className="s1-card__thumb-icon">{meta.icon}</span>
        <div className="s1-card__thumb-overlay" />
      </div>
      <div className="s1-card__body">
        <h3 className="s1-card__title">{project.title}</h3>
        <div className="s1-card__meta">
          {brand && (
            <span className={`brand-badge brand-badge--${brand}`}>
              {brand === 'ab' ? 'AB' : brand}
            </span>
          )}
          <StatusBadge status={project.status} />
        </div>
        <div className="s1-card__footer">
          <span className="s1-card__age">
            {project.shipState ? `Done · ${project.shipState.replace(/_/g, ' ')}` : timeAgo(project.createdAt)}
          </span>
          <span className="s1-card__action">{meta.action} →</span>
        </div>
      </div>
    </button>
  );
};

interface Props { user: User | null; }

const VideoEditorModule: React.FC<Props> = ({ user }) => {
  const api = useVideoEditorApi(user);

  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProjects(await api.listProjects());
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (user) fetchProjects(); }, [user, fetchProjects]);

  const createProject = useCallback(async () => {
    const title = window.prompt('Name your video project:');
    if (!title || !title.trim()) return;
    try {
      setBusy(true); setError(null);
      const p = await api.createProject({ title: title.trim() });
      setSelectedId(p._id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create project');
    } finally { setBusy(false); }
  }, [api]);

  const createSample = useCallback(async () => {
    try {
      setBusy(true); setError(null);
      const p = await api.createSample();
      setSelectedId(p._id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create the sample project');
    } finally { setBusy(false); }
  }, [api]);

  // ── Workspace (surfaces 2–7) ──
  if (selectedId) {
    return (
      <ProjectWorkspace
        projectId={selectedId}
        user={user}
        onBack={() => { setSelectedId(null); fetchProjects(); }}
      />
    );
  }

  // ── Surface 1: project list ──
  const counts: Record<string, number> = {};
  for (const f of FILTERS) {
    counts[f.key] = f.statuses === null
      ? projects.length
      : projects.filter(p => f.statuses!.includes(p.status)).length;
  }
  const activeDef = FILTERS.find(f => f.key === activeFilter) || FILTERS[0];
  const visible = projects
    .filter(p => activeDef.statuses === null || activeDef.statuses.includes(p.status))
    .filter(p => !search.trim() || p.title.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="video-editor">
      <div className="s1-header">
        <div className="s1-header__titleblock">
          <h1>Video Editor</h1>
          <p>
            Make something good. Drop in raw footage, talk through what you want,
            the AI cuts the first pass — you decide if it's any good.
          </p>
        </div>
        <button className="s1-header__cta" onClick={createProject} disabled={busy}>
          <span className="s1-header__cta-icon">+</span>
          {busy ? 'Working…' : 'Start new video'}
        </button>
      </div>

      <div className="s1-filters" role="tablist">
        {FILTERS.map(f => (
          <button
            key={f.key}
            role="tab"
            aria-selected={activeFilter === f.key}
            className={`s1-filter${activeFilter === f.key ? ' s1-filter--active' : ''}`}
            onClick={() => setActiveFilter(f.key)}
          >
            {f.label} <span className="s1-filter__count">{counts[f.key]}</span>
          </button>
        ))}
        <div className="s1-filters__spacer" />
        <input
          className="s1-search"
          type="search"
          placeholder="Search projects…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="s1-grid">
        {loading && <div className="ve-state">Loading projects…</div>}

        {!loading && error && (
          <div className="ve-state ve-state--error">
            {error}
            <div style={{ marginTop: 12 }}>
              <button className="s1-empty__cta" onClick={fetchProjects}>Try again</button>
            </div>
          </div>
        )}

        {!loading && !error && projects.length === 0 && (
          <div className="s1-empty">
            <div className="s1-empty__icon">🎬</div>
            <h2 className="s1-empty__title">Make your first video</h2>
            <p className="s1-empty__copy">
              Start a project and drop in footage — or try a ready-made sample to
              see the whole flow without uploading anything.
            </p>
            <div className="ve-actions" style={{ justifyContent: 'center' }}>
              <button className="s1-empty__cta" onClick={createProject} disabled={busy}>
                Start new video
              </button>
              <button className="ve-btn" onClick={createSample} disabled={busy}>
                {busy ? 'Setting up…' : 'Try a sample project'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && projects.length > 0 && visible.length === 0 && (
          <div className="ve-state ve-muted">No projects match this filter.</div>
        )}

        {!loading && !error && visible.map(p => (
          <ProjectCard key={p._id} project={p} onOpen={setSelectedId} />
        ))}
      </div>
    </div>
  );
};

export default VideoEditorModule;
